import React, { useEffect, useMemo } from 'react';
import { useToast, useLists } from '../../context';
import { useLang, useAutoTranslate, SOURCE_LANG } from '../../i18n';
import { useChecklist } from '../../hooks/useChecklist';
import { useListNames } from '../../hooks/useListNames';
import { useSelection } from '../../hooks/useSelection';
import { useSaveFailed } from '../../hooks/useSaveFailed';
import { itemStatus, isFlagged } from '../../logic/items';
import { timeMs, fmtDateShort, localDateISO } from '../../logic/dates';
import { normalizeName } from '../../logic/names';
import { learnedStores } from '../../logic/stores';
import { planChecklistAdds } from '../../logic/checklists';
import { STATUS, STATUS_META } from '../../config/lists';
import { bulkSetStatus, restoreStatuses } from '../../data/items';
import { addToChecklist, undoAddToChecklist } from '../../data/checklists';
import { EmptyState } from '../shared/EmptyState';

const NO_NAMES = [];

/**
 * Review — what couldn't be sourced: items on a buy-list marked 🔍 not found or ⛔ out of
 * stock, newest first, grouped by status. A manager ticks them refilled or queues them on
 * the Shopping list (one write, undo-able).
 */
export function ReviewTab({ items, author, onGoToday }) {
  const notify = useToast();
  const failed = useSaveFailed();
  const { t, ln, lang } = useLang();
  const { catById } = useLists();
  const { name: listName } = useListNames();
  const today = localDateISO();
  const flagged = useMemo(
    () => items
      .filter(i => catById(i.category).sourceable && isFlagged(i))
      .sort((a, b) => b.date.localeCompare(a.date) || timeMs(b.flaggedAt) - timeMs(a.flaggedAt)),
    [items, catById],
  );
  const byId = useMemo(() => new Map(flagged.map(i => [i.id, i])), [flagged]);
  useAutoTranslate(useMemo(() => (lang === SOURCE_LANG ? NO_NAMES : flagged.map(i => i.name)), [lang, flagged]));

  // The Shopping list, so "Add to shopping" re-flags what's there and files new items
  // under the store they were last bought from.
  const { items: shopItems, log: shopLog } = useChecklist('shopping');
  const learnedShop = useMemo(() => learnedStores(shopLog), [shopLog]);

  // Picks drop out when an item is handled on another device.
  const { selected, toggle, toggleAll, clear, keepOnly } = useSelection();
  useEffect(() => { keepOnly([...byId.keys()]); }, [byId, keepOnly]);

  const refill = ids => {
    if (!ids.length) return;
    const prev = ids.filter(id => byId.has(id)).map(id => ({ id, status: itemStatus(byId.get(id)) }));
    bulkSetStatus(ids, STATUS.REFILLED, author).catch(failed);
    clear();
    notify(t('review.notifyRefilled', { n: ids.length }), () => restoreStatuses(prev, author).catch(failed));
  };

  // Queue flagged items onto the standing Shopping list to buy next run — one write.
  // Undo deletes what it created and takes re-flagged items back off the list.
  const addToShopping = ids => {
    const its = ids.map(id => byId.get(id)).filter(Boolean);
    if (!its.length) return;
    const plan = planChecklistAdds({
      names: its.map(i => i.name), items: shopItems, fillStoreOnly: true, today,
      storeFor: name => learnedShop.get(normalizeName(name)) || '',
    });
    const { createdIds, saved } = addToChecklist({ list: 'shopping', ...plan, firstOrder: (shopItems.length + 1) * 10, addedBy: author, today });
    saved.catch(failed);
    clear();
    const undo = {
      createdIds,
      unflagIds: plan.updates.filter(u => !u.wasNeeded).map(u => u.id),
      rehideIds: plan.updates.filter(u => u.wasHidden).map(u => u.id),
    };
    const label = its.length === 1 ? ln(its[0].name) : t('review.nItems', { n: its.length });
    const canUndo = createdIds.length > 0 || undo.unflagIds.length > 0;
    notify(t('review.notifyAdded', { label }), canUndo ? () => undoAddToChecklist(undo).catch(failed) : undefined);
  };

  if (flagged.length === 0) {
    return (
      <EmptyState icon="✅" title={t('review.emptyTitle')}
        sub={t('review.emptyBody', { notFound: t('status.not_found'), oos: t('status.oos') })}>
        <button className="do-focus" style={{ marginTop: 16 }} onClick={onGoToday}>{t('review.backToToday')}</button>
      </EmptyState>
    );
  }

  const selectedIds = [...selected];
  const groups = [STATUS.NOT_FOUND, STATUS.OOS].map(status => [status, flagged.filter(i => itemStatus(i) === status)]);

  return (
    <div className="do-review">
      <p className="do-review__intro">{t('review.intro')}</p>

      {selectedIds.length > 0 && (
        <div className="do-rev-bulk">
          <span className="do-rev-bulk__count">{t('review.selected', { n: selectedIds.length })}</span>
          <button className="do-rev-bulk__btn" onClick={() => refill(selectedIds)}>{t('review.markRefilled')}</button>
          <button className="do-rev-bulk__btn do-rev-bulk__btn--alt" onClick={() => addToShopping(selectedIds)}>{t('review.addToShopping')}</button>
          <button className="do-rev-bulk__clear" onClick={clear}>{t('common.clear')}</button>
        </div>
      )}

      {groups.map(([status, list]) => {
        if (list.length === 0) return null;
        const m = STATUS_META[status];
        const statusLabel = t(`status.${status}`);
        const ids = list.map(i => i.id);
        return (
          <div key={status} className="do-rev-group" style={{ '--sc': m.color }}>
            <div className="do-rev-group__head">
              <input type="checkbox" className="do-rev-cb" checked={ids.every(id => selected.has(id))}
                onChange={() => toggleAll(ids)} aria-label={t('review.selectAll', { label: statusLabel })} />
              <span className="do-rev-group__title">{m.icon} {statusLabel} <b>{list.length}</b></span>
              <button className="do-rev-group__all" onClick={() => refill(ids)}>{t('review.refillAll')}</button>
            </div>
            <div className="do-rev-group__items">
              {list.map(it => {
                const c = catById(it.category);
                return (
                  <label key={it.id} className={`do-rev-item${selected.has(it.id) ? ' do-rev-item--sel' : ''}`}>
                    <input type="checkbox" className="do-rev-cb" checked={selected.has(it.id)} onChange={() => toggle(it.id)} />
                    <span className="do-rev-item__cat" style={{ '--cc': c.color }} title={listName(c)}>{c.icon}</span>
                    <span className="do-rev-item__name">{ln(it.name)}{it.qty && <b className="do-rev-item__qty">{it.qty}</b>}</span>
                    <span className="do-rev-item__meta">
                      {it.date !== today ? `${fmtDateShort(it.date)} · ` : ''}
                      {it.flaggedBy ? t('review.by', { name: it.flaggedBy.split(/[ @]/)[0] }) : ''}
                    </span>
                    <button type="button" className="do-rev-item__shop"
                      onClick={e => { e.preventDefault(); addToShopping([it.id]); }} title={t('review.shopTitle')}>{t('review.shop')}</button>
                    <button type="button" className="do-rev-item__refill"
                      onClick={e => { e.preventDefault(); refill([it.id]); }}>{t('review.refilled')}</button>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
